import { map } from "rxjs/operators";

import { Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { UserMapper } from "./user.mapper";

import { environment } from "environments/environment";
import { CrudAbstractRepository } from "../crud-abstract/crud-abstract.repository";
import { User, UserStats } from "./user.model";

export interface McpTokenStatus {
  connected: boolean;
  createdAt: string | null;
}

export interface McpTokenIssued {
  url: string;
  createdAt: string | null;
}

@Injectable()
export class UserRepository extends CrudAbstractRepository {
  protected httpClient = inject(HttpClient);

  public getMapper(): any {
    return UserMapper.map;
  }

  public getEndpoint(): string {
    return "user";
  }

  public login({ username, password }): Observable<object> {
    return this.httpClient.post(
      environment.apiUrl + "/login_check",
      {
        username,
        password,
      },
      {
        withCredentials: true,
      },
    );
  }

  public verifyTwoFactor({ code }): Observable<object> {
    return this.httpClient.post(
      environment.apiUrl + "/2fa_check",
      {
        data: { authCode: code },
      },
      {
        withCredentials: true,
      },
    );
  }

  public changePassword(
    userId: number,
    oldPassword: string,
    newPassword: string,
  ): Observable<object> {
    return this.http.patch(
      `${environment.apiUrl}/${this.getEndpoint()}/${userId}/changePassword`,
      {
        oldPassword: oldPassword,
        newPassword: newPassword,
      },
    );
  }

  public register(data): Observable<object> {
    return this.httpClient.post(`${environment.apiUrl}/register`, {
      user: {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        notes: data.notes,
      },
      organization: {
        name: data.organizationName,
      },
    });
  }

  public resetPasswordRequest(data: {
    [key: string]: any;
  }): Observable<object> {
    return this.httpClient.post(`${environment.apiUrl}/send-email`, {
      username: data.email,
    });
  }

  public resetPassword(
    data: { [key: string]: any },
    urlToken: string,
  ): Observable<object> {
    return this.httpClient.post(`${environment.apiUrl}/reset/${urlToken}`, {
      plainPassword: {
        first: data.password,
        second: data.passwordConfirm,
      },
    });
  }

  public join(
    data: { [key: string]: any },
    urlToken: string,
  ): Observable<object> {
    return this.httpClient.post(`${environment.apiUrl}/join/${urlToken}`, {
      plainPassword: {
        first: data.password,
        second: data.passwordConfirm,
      },
    });
  }

  public activate(
    data: { [key: string]: any },
    urlToken: string,
  ): Observable<object> {
    return this.httpClient.post(`${environment.apiUrl}/activate/${urlToken}`, {
      plainPassword: {
        first: data.password,
        second: data.passwordConfirm,
      },
    });
  }

  public checkJoinToken(token: string): Observable<object> {
    return this.httpClient.get(`${environment.apiUrl}/join-check/${token}`);
  }

  public checkResetToken(token: string): Observable<object> {
    return this.httpClient.get(`${environment.apiUrl}/reset-check/${token}`);
  }

  public getUser(): Observable<User> {
    return this.http
      .get(environment.apiUrl + "/me")
      .pipe(map(this.getMapper()));
  }

  public updateEmailIntegration(
    userId: number,
    mailIntegrationEnabled: boolean,
    emailIntegrationType: string,
    mailUsername?: string,
    mailPassword?: string,
    mailImapPath?: string,
    mailMsGraphFolder?: string,
  ): Observable<object> {
    return this.http.patch(
      `${environment.apiUrl}/${this.getEndpoint()}/${userId}`,
      {
        mailIntegrationEnabled: mailIntegrationEnabled,
        emailIntegrationType: emailIntegrationType,
        mailUsername: mailUsername,
        mailPassword: mailPassword,
        mailImapPath: mailImapPath,
        mailMsGraphFolder: mailMsGraphFolder,
      },
    );
  }

  public getMailboxFolders(user: User): Observable<object> {
    return this.http.get(
      `${environment.apiUrl}/user/${user.getId()}/mailboxFolders`,
    );
  }

  public getProgress(): Observable<object> {
    return this.http.get(`${environment.apiUrl}/me/progress`);
  }

  public getStats(): Observable<UserStats> {
    return this.http.get<UserStats>(`${environment.apiUrl}/user/stats`);
  }

  public initiateMsGraphAuthorize(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${environment.apiUrl}/me/ms-graph/authorize`,
    );
  }

  public isLoggedInToMsGraph(): Observable<object> {
    return this.http.get(`${environment.apiUrl}/me/ms-graph-is-logged-in`);
  }

  public disconnectMsGraph(): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/me/ms-graph`);
  }

  public getMcpToken(): Observable<McpTokenStatus> {
    return this.http.get<McpTokenStatus>(`${environment.apiUrl}/me/mcp-token`);
  }

  public createMcpToken(): Observable<McpTokenIssued> {
    return this.http.post<McpTokenIssued>(
      `${environment.apiUrl}/me/mcp-token`,
      {},
    );
  }

  public deleteMcpToken(): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/me/mcp-token`);
  }
}
